/**
 * Judgment Index model — pure interpretation logic.
 *
 * Converts raw Judgment Index scores (IES primaries + balance meters) into
 * bands, advisories, and team-level aggregates. No I/O, no React, no store —
 * every function is deterministic and unit-testable.
 *
 * Advisory only: nothing here mutates rankings, scores, or weights.
 */

import type {
  JudgmentIES,
  JudgmentIESBands,
  JudgmentBalance,
  JudgmentIndexProfile,
  ScoreBand,
  BalanceStatus,
} from '../types/prioritization/judgmentIndex';
import {
  WORK_SIDE_META,
  SELF_SIDE_META,
  BALANCE_META,
} from '../types/prioritization/judgmentIndex';

// ── Band thresholds for IES primary scores ─────────────────────────────────────
// Derived from visual analysis of the official Score Sheet band zones.
// A certified practitioner can confirm exact cut-points from the reference material.

export const IES_STRONG    = 18;
export const IES_GOOD      = 7;
export const IES_ATTENTION = 4;
export const IES_DEVELOPING = 1;

export function iesScoreBand(score: number): ScoreBand {
  if (score >= IES_STRONG)    return 'strong';
  if (score >= IES_GOOD)      return 'good';
  if (score >= IES_ATTENTION) return 'attention';
  if (score >= IES_DEVELOPING) return 'developing';
  return 'needs_work';
}

// ── Balance meter thresholds ───────────────────────────────────────────────────
// Per the official instrument reference: −5 to +5 is properly balanced.

export const BALANCE_THRESHOLD = 5;

export function balanceStatus(value: number): BalanceStatus {
  if (value > BALANCE_THRESHOLD)  return 'over';
  if (value < -BALANCE_THRESHOLD) return 'under';
  return 'balanced';
}

// ── Composite score ────────────────────────────────────────────────────────────

/** Mean of the 6 primary IES scores, rounded. Used as a single composite index. */
export function overallScore(workSide: JudgmentIES, selfSide: JudgmentIES): number {
  const values = [
    workSide.intrinsic, workSide.extrinsic, workSide.systemic,
    selfSide.intrinsic, selfSide.extrinsic, selfSide.systemic,
  ];
  return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
}

// ── Empty seeds (form defaults) ────────────────────────────────────────────────

export function emptyIES(): JudgmentIES {
  return { intrinsic: 0, extrinsic: 0, systemic: 0 };
}

export function emptyBalance(): JudgmentBalance {
  return { people: 0, work: 0, ideas: 0, esteem: 0, concept: 0, image: 0 };
}

export function emptyBands(): JudgmentIESBands {
  return { intrinsic: null, extrinsic: null, systemic: null };
}

// ── Band resolution ────────────────────────────────────────────────────────────

/**
 * Resolves the effective band for one IES score. When the practitioner has
 * entered the band label directly from the report, that stored value takes
 * precedence over the computed threshold — avoiding errors from proprietary
 * cut-points that aren't publicly published.
 */
export function resolveIESBand(score: number, stored?: ScoreBand | null): ScoreBand {
  return stored ?? iesScoreBand(score);
}

// ── Per-profile advisories ─────────────────────────────────────────────────────

export type AdvisoryKind = 'strength' | 'blind_spot' | 'balance';

export interface JudgmentAdvisory {
  key: string;
  label: string;
  kind: AdvisoryKind;
  message: string;
}

/**
 * Produces prioritization advisories for one profile:
 *  - `strength`   when an IES score band is `strong`
 *  - `blind_spot` when an IES score band is `attention`, `developing`, or `needs_work`
 *  - `balance`    when a balance meter exceeds ±5
 * `good` IES scores are intentionally quiet to keep guidance signal-rich.
 */
export function profileAdvisories(profile: JudgmentIndexProfile): JudgmentAdvisory[] {
  const out: JudgmentAdvisory[] = [];

  const iesChecks: { key: string; score: number; stored?: ScoreBand | null; meta: { label: string; strengthInPrioritization: string; blindSpotInPrioritization: string } }[] = [
    { key: 'work_intrinsic', score: profile.workSide.intrinsic, stored: profile.workSideBands?.intrinsic, meta: WORK_SIDE_META.intrinsic },
    { key: 'work_extrinsic', score: profile.workSide.extrinsic, stored: profile.workSideBands?.extrinsic, meta: WORK_SIDE_META.extrinsic },
    { key: 'work_systemic',  score: profile.workSide.systemic,  stored: profile.workSideBands?.systemic,  meta: WORK_SIDE_META.systemic  },
    { key: 'self_intrinsic', score: profile.selfSide.intrinsic, stored: profile.selfSideBands?.intrinsic, meta: SELF_SIDE_META.intrinsic },
    { key: 'self_extrinsic', score: profile.selfSide.extrinsic, stored: profile.selfSideBands?.extrinsic, meta: SELF_SIDE_META.extrinsic },
    { key: 'self_systemic',  score: profile.selfSide.systemic,  stored: profile.selfSideBands?.systemic,  meta: SELF_SIDE_META.systemic  },
  ];

  for (const { key, score, stored, meta } of iesChecks) {
    const band = resolveIESBand(score, stored);
    if (band === 'strong') {
      out.push({ key, label: meta.label, kind: 'strength', message: meta.strengthInPrioritization });
    } else if (band !== 'good') {
      out.push({ key, label: meta.label, kind: 'blind_spot', message: meta.blindSpotInPrioritization });
    }
  }

  for (const [balKey, value] of Object.entries(profile.balance) as [keyof JudgmentBalance, number][]) {
    const status = balanceStatus(value);
    if (status === 'balanced') continue;
    const meta = BALANCE_META[balKey];
    out.push({
      key: `balance_${balKey}`,
      label: meta.label,
      kind: 'balance',
      message: status === 'over' ? meta.overMeaning : meta.underMeaning,
    });
  }

  return out;
}

// ── Team aggregate ─────────────────────────────────────────────────────────────

export interface TeamIESStat {
  key: string;
  label: string;
  meanScore: number;
  band: ScoreBand;
  strongCount: number;
  lowCount: number;
}

export interface TeamBalanceStat {
  key: keyof JudgmentBalance;
  label: string;
  side: 'work' | 'self';
  meanValue: number;
  status: BalanceStatus;
}

export interface TeamJudgmentAggregate {
  profileCount: number;
  workSideMean: JudgmentIES;
  selfSideMean: JudgmentIES;
  balanceMean: JudgmentBalance;
  overallMean: number;
  iesStats: TeamIESStat[];
  balanceStats: TeamBalanceStat[];
  collectiveStrengths: TeamIESStat[];
  collectiveBlindSpots: TeamIESStat[];
  balanceWatchOuts: TeamBalanceStat[];
  balanceSummary: string;
}

function mean(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

function meanIES(profiles: JudgmentIndexProfile[], side: 'workSide' | 'selfSide'): JudgmentIES {
  return {
    intrinsic: Math.round(mean(profiles.map((p) => p[side].intrinsic))),
    extrinsic: Math.round(mean(profiles.map((p) => p[side].extrinsic))),
    systemic:  Math.round(mean(profiles.map((p) => p[side].systemic))),
  };
}

function meanBalance(profiles: JudgmentIndexProfile[]): JudgmentBalance {
  return {
    people:  Math.round(mean(profiles.map((p) => p.balance.people))),
    work:    Math.round(mean(profiles.map((p) => p.balance.work))),
    ideas:   Math.round(mean(profiles.map((p) => p.balance.ideas))),
    esteem:  Math.round(mean(profiles.map((p) => p.balance.esteem))),
    concept: Math.round(mean(profiles.map((p) => p.balance.concept))),
    image:   Math.round(mean(profiles.map((p) => p.balance.image))),
  };
}

const LOW_BANDS = new Set<ScoreBand>(['attention', 'developing', 'needs_work']);

export function aggregateTeam(profiles: JudgmentIndexProfile[]): TeamJudgmentAggregate {
  const profileCount = profiles.length;

  if (profileCount === 0) {
    const zero: JudgmentIES = emptyIES();
    const zeroBal: JudgmentBalance = emptyBalance();
    return {
      profileCount: 0,
      workSideMean: zero,
      selfSideMean: zero,
      balanceMean: zeroBal,
      overallMean: 0,
      iesStats: [],
      balanceStats: [],
      collectiveStrengths: [],
      collectiveBlindSpots: [],
      balanceWatchOuts: [],
      balanceSummary: 'No Judgment Index profiles recorded yet for this group.',
    };
  }

  const workSideMean = meanIES(profiles, 'workSide');
  const selfSideMean = meanIES(profiles, 'selfSide');
  const balanceMean  = meanBalance(profiles);
  const overallMean  = overallScore(workSideMean, selfSideMean);

  const iesChecks: { key: string; workSideKey?: keyof JudgmentIES; selfSideKey?: keyof JudgmentIES; meta: { label: string } }[] = [
    { key: 'work_intrinsic', workSideKey: 'intrinsic', meta: WORK_SIDE_META.intrinsic },
    { key: 'work_extrinsic', workSideKey: 'extrinsic', meta: WORK_SIDE_META.extrinsic },
    { key: 'work_systemic',  workSideKey: 'systemic',  meta: WORK_SIDE_META.systemic  },
    { key: 'self_intrinsic', selfSideKey: 'intrinsic', meta: SELF_SIDE_META.intrinsic },
    { key: 'self_extrinsic', selfSideKey: 'extrinsic', meta: SELF_SIDE_META.extrinsic },
    { key: 'self_systemic',  selfSideKey: 'systemic',  meta: SELF_SIDE_META.systemic  },
  ];

  const iesStats: TeamIESStat[] = iesChecks.map(({ key, workSideKey, selfSideKey, meta }) => {
    const scores = profiles.map((p) =>
      workSideKey ? p.workSide[workSideKey] : p.selfSide[selfSideKey!],
    );
    const meanScore = Math.round(mean(scores));
    const resolvedBands = profiles.map((p, i) => {
      const stored = workSideKey
        ? p.workSideBands?.[workSideKey]
        : p.selfSideBands?.[selfSideKey!];
      return resolveIESBand(scores[i], stored);
    });
    return {
      key,
      label: meta.label,
      meanScore,
      band: iesScoreBand(meanScore),
      strongCount: resolvedBands.filter((b) => b === 'strong').length,
      lowCount: resolvedBands.filter((b) => LOW_BANDS.has(b)).length,
    };
  });

  const balanceStats: TeamBalanceStat[] = (
    Object.keys(balanceMean) as (keyof JudgmentBalance)[]
  ).map((k) => ({
    key: k,
    label: BALANCE_META[k].label,
    side: BALANCE_META[k].side,
    meanValue: balanceMean[k],
    status: balanceStatus(balanceMean[k]),
  }));

  const collectiveStrengths = iesStats.filter((s) => s.band === 'strong');
  const collectiveBlindSpots = iesStats.filter((s) => LOW_BANDS.has(s.band));
  const balanceWatchOuts = balanceStats.filter((s) => s.status !== 'balanced');

  return {
    profileCount,
    workSideMean,
    selfSideMean,
    balanceMean,
    overallMean,
    iesStats,
    balanceStats,
    collectiveStrengths,
    collectiveBlindSpots,
    balanceWatchOuts,
    balanceSummary: buildBalanceSummary(profileCount, collectiveStrengths, collectiveBlindSpots),
  };
}

function buildBalanceSummary(
  count: number,
  strengths: TeamIESStat[],
  blindSpots: TeamIESStat[],
): string {
  if (count === 0) return 'No Judgment Index profiles recorded yet for this group.';
  const parts: string[] = [];
  if (strengths.length > 0) {
    parts.push(`Collective strength in ${joinLabels(strengths.map((s) => s.label))}.`);
  }
  if (blindSpots.length > 0) {
    parts.push(
      `Shared gap in ${joinLabels(blindSpots.map((s) => s.label))} — ` +
        'consider assigning an owner or dedicating extra deliberation where that lens matters.',
    );
  }
  if (parts.length === 0) {
    parts.push(
      'Judgment is broadly balanced across the group, with no dimension standing out as a collective strength or gap.',
    );
  }
  return parts.join(' ');
}

function joinLabels(labels: string[]): string {
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
}
